import { useRef, useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { api, type Asset } from '@/lib/api';
import { Icon } from './ui/Icon';

type Props = {
  projectId: string;
  onUploaded?: (asset: Asset) => void;
};

export function MediaImport({ projectId, onUploaded }: Props) {
  const inputRef = useRef<HTMLInputElement>(null);
  const qc = useQueryClient();
  const [progress, setProgress] = useState<string | null>(null);

  const upload = useMutation({
    mutationFn: async (file: File) => {
      setProgress(`Laddar upp ${file.name}...`);
      const asset = await api.assets.upload(projectId, file);
      return asset;
    },
    onSuccess: (asset) => {
      setProgress(null);
      qc.invalidateQueries({ queryKey: ['project-assets', projectId] });
      onUploaded?.(asset);
    },
    onError: (err) => {
      setProgress(`Fel: ${err instanceof Error ? err.message : String(err)}`);
      setTimeout(() => setProgress(null), 4000);
    },
  });

  return (
    <>
      <input
        ref={inputRef}
        type="file"
        accept="video/*,image/*,audio/*"
        className="hidden"
        onChange={(e) => {
          const file = e.target.files?.[0];
          if (file) upload.mutate(file);
          e.target.value = '';
        }}
      />
      <button
        onClick={() => inputRef.current?.click()}
        disabled={upload.isPending}
        className="btn btn-ghost"
        title="Importera media"
      >
        {upload.isPending ? (
          <Icon name="loader-2" size={14} className="animate-spin" />
        ) : (
          <Icon name="upload" size={14} />
        )}
        <span className="text-xs">Importera</span>
      </button>
      {progress && (
        <span className="text-xs text-text-muted ml-2 truncate max-w-[200px]">{progress}</span>
      )}
    </>
  );
}
